import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Alert, 
  AlertTitle, 
  Snackbar,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
  Close
} from '@mui/icons-material';

const FeedbackNotification = ({
  open,
  message,
  severity = 'info',
  title,
  duration = 6000,
  onClose,
  position = 'top-right',
  showProgress = false,
  persistent = false,
  actions,
  ...props
}) => {
  const getPositionStyles = () => {
    const positions = {
      'top-right': { top: 24, right: 24 },
      'top-left': { top: 24, left: 24 },
      'top-center': { top: 24, left: '50%', transform: 'translateX(-50%)' },
      'bottom-right': { bottom: 24, right: 24 },
      'bottom-left': { bottom: 24, left: 24 },
      'bottom-center': { bottom: 24, left: '50%', transform: 'translateX(-50%)' }
    };
    return positions[position] || positions['top-right'];
  };

  const notificationVariants = {
    initial: {
      opacity: 0,
      y: position.includes('top') ? -100 : 100,
      x: position.includes('center') ? 0 : (position.includes('right') ? 100 : -100),
      scale: 0.8,
      rotate: 0
    },
    animate: {
      opacity: 1,
      y: 0,
      x: position.includes('center') && !position.includes('top') && !position.includes('bottom') ? 0 : 0,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30,
        duration: 0.5
      }
    },
    exit: {
      opacity: 0,
      y: position.includes('top') ? -100 : 100,
      x: position.includes('right') ? 100 : (position.includes('left') ? -100 : 0),
      scale: 0.8,
      rotate: position.includes('right') ? 10 : (position.includes('left') ? -10 : 0),
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  const progressVariants = {
    initial: { width: '100%' },
    animate: { 
      width: '0%',
      transition: { 
        duration: duration / 1000,
        ease: "linear"
      }
    }
  };

  const iconVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: {
        delay: 0.2,
        type: "spring",
        stiffness: 600,
        damping: 25
      }
    }
  };

  const getIcon = () => {
    const icons = {
      success: <CheckCircle />,
      error: <Error />,
      warning: <Warning />,
      info: <Info />
    };
    return icons[severity] || icons.info;
  };

  const getColor = () => {
    const colors = {
      success: '#4caf50',
      error: '#f44336', 
      warning: '#ff9800',
      info: '#2196f3'
    };
    return colors[severity] || colors.info;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          variants={notificationVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{
            position: 'fixed',
            zIndex: 9999,
            maxWidth: 400,
            minWidth: 300,
            ...getPositionStyles()
          }}
        >
          <Alert
            severity={severity}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {actions}
                {!persistent && (
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      size="small"
                      onClick={onClose}
                      sx={{ color: 'inherit' }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </motion.div>
                )}
              </Box>
            }
            icon={
              <motion.div variants={iconVariants}>
                {getIcon()}
              </motion.div>
            }
            sx={{
              '& .MuiAlert-icon': {
                alignItems: 'center'
              },
              borderRadius: 2,
              boxShadow: 3
            }}
            {...props}
          >
            {title && (
              <AlertTitle>
                <Typography variant="subtitle2" fontWeight={600}>
                  {title}
                </Typography>
              </AlertTitle>
            )}
            <Typography variant="body2">
              {message}
            </Typography>
            
            {showProgress && !persistent && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '0 0 8px 8px',
                  overflow: 'hidden'
                }}
              >
                <motion.div
                  variants={progressVariants}
                  initial="initial"
                  animate="animate"
                  style={{
                    height: '100%',
                    backgroundColor: getColor(),
                    borderRadius: '0 0 8px 8px'
                  }}
                />
              </Box>
            )}
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ToastStack = ({ toasts, onRemove }) => {
  return (
    <Box sx={{ position: 'fixed', top: 24, right: 24, zIndex: 9999 }}>
      <AnimatePresence>
        {toasts.map((toast, index) => (
          <motion.div
            key={toast.id}
            initial={{ 
              opacity: 0, 
              y: -50,
              x: 100,
              scale: 0.8 
            }}
            animate={{ 
              opacity: 1, 
              y: index * 80,
              x: 0,
              scale: 1,
              transition: {
                type: "spring",
                stiffness: 500,
                damping: 30
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -50,
              x: 100,
              scale: 0.8,
              transition: {
                duration: 0.3
              }
            }}
            layout
            style={{ 
              position: 'absolute',
              width: 350
            }}
          >
            <FeedbackNotification
              open={true}
              {...toast}
              onClose={() => onRemove(toast.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </Box>
  );
};

const SuccessRipple = ({ children, trigger = false }) => {
  const rippleVariants = {
    initial: {
      scale: 0,
      opacity: 0
    },
    animate: {
      scale: [0, 1.2, 1.5],
      opacity: [0, 0.4, 0],
      transition: {
        duration: 0.6,
        times: [0, 0.5, 1],
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {children}
      <AnimatePresence>
        {trigger && (
          <motion.div
            variants={rippleVariants}
            initial="initial"
            animate="animate"
            exit="initial"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              pointerEvents: 'none',
              zIndex: 0,
              opacity: 0.3
            }}
          />
        )}
      </AnimatePresence>
    </Box>
  );
};

const LoadingPulse = ({ 
  children, 
  loading = false, 
  pulseColor = 'rgba(2, 62, 138, 0.3)' 
}) => {
  const pulseVariants = {
    rest: {
      scale: 1,
      boxShadow: 'none'
    },
    loading: {
      scale: [1, 1.05, 1],
      boxShadow: [
        'none',
        `0 0 20px ${pulseColor}`,
        'none'
      ],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div
      variants={pulseVariants}
      animate={loading ? "loading" : "rest"}
      style={{
        display: 'inline-block',
        borderRadius: 'inherit'
      }}
    >
      {children}
    </motion.div>
  );
};

export { 
  FeedbackNotification, 
  ToastStack, 
  SuccessRipple, 
  LoadingPulse 
};